import { writable, derived } from 'svelte/store';
import type { GrammarError, AIComment } from '$lib/types/grammar';
import type { AISettings } from '$lib/types/ai';
import { AIAnalysisService } from '$lib/services/ai/AIAnalysisService';
import { getAIAnalysisServiceV2 } from '$lib/services/ai/AIAnalysisServiceV2';
import { settingsStore } from './settings';
import { get } from 'svelte/store';
import { SubscriptionService } from '$lib/services/subscription/SubscriptionService';
import { openUpgradePrompt } from './subscription';
import { toast } from './toast';

/**
 * Store for managing AI suggestions and comments on grammar errors
 */

interface AIAnalysisState {
  /** Map of error ID to AI comment */
  comments: Map<string, AIComment>;
  /** Set of error IDs currently being analyzed */
  analyzingErrors: Set<string>;
  /** Last analysis error */
  lastError: string | null;
  /** Whether AI analysis is enabled */
  isEnabled: boolean;
}

function createAISuggestionsStore() {
  const { subscribe, set, update } = writable<AIAnalysisState>({
    comments: new Map(),
    analyzingErrors: new Set(),
    lastError: null,
    isEnabled: false
  });

  let aiService: AIAnalysisService | null = null;
  
  // Feature flag for using new AI service with enhanced error handling
  // Default to true since V2 has better error handling
  const useNewAIService = import.meta.env.VITE_USE_NEW_AI_SERVICE !== 'false';

  // Helper function to detect provider from model name
  function detectProviderFromModel(modelName: string): string {
    // OpenRouter models (with slash notation)
    if (modelName.includes('/')) {
      return 'openrouter';
    }
    
    // Claude models
    if (modelName.startsWith('claude-')) {
      return 'anthropic';
    }
    
    // GPT models (without slash)
    if (modelName.startsWith('gpt-')) {
      return 'openai';
    }
    
    // Gemini models
    if (modelName.startsWith('gemini-')) {
      return 'google';
    }
    
    // Upstage models
    if (modelName.startsWith('solar-')) {
      return 'upstage';
    }
    
    // Default to openrouter for unknown models
    return 'openrouter';
  }

  // Initialize AI service when settings change
  settingsStore.subscribe((settings) => {
    // Check if AI is globally enabled first
    if (!settings.ai?.enabled) {
      aiService = null;
      update(state => ({ ...state, isEnabled: false }));
      return;
    }

    // Use grammar check specific AI model if available, otherwise fall back to general AI model
    const grammarCheckModel = settings.unifiedCheck?.aiModel || settings.ai?.selectedModel || 'openai/gpt-oss-120b';

    // Intelligently determine the provider based on the model name
    const detectedProvider = detectProviderFromModel(grammarCheckModel);
    let provider = detectedProvider;
    let apiKey = '';
    
    // Get the appropriate API key based on the provider
    // First try to get from aiProviders array (where it's actually stored)
    const providerConfig = settings.aiProviders?.find(p => p.id === provider);
    if (providerConfig && providerConfig.enabled && providerConfig.apiKey) {
      apiKey = providerConfig.apiKey;
    } else {
      // Fallback to legacy AI settings
      switch (provider) {
        case 'openai':
          apiKey = settings.ai?.openaiApiKey || '';
          break;
        case 'anthropic':
          apiKey = settings.ai?.anthropicApiKey || '';
          break;
        case 'google':
          apiKey = settings.ai?.googleApiKey || '';
          break;
        case 'upstage':
          apiKey = settings.ai?.upstageApiKey || '';
          break;
        case 'openrouter':
          // OpenRouter uses proxy, no API key needed
          apiKey = '';
          break;
        default:
          apiKey = '';
      }
    }
    
    // Check if the provider has a valid configuration
    const hasValidConfig = provider === 'openrouter' || (apiKey && apiKey.trim() !== '');

    if (!hasValidConfig) {
      aiService = null;
      update(state => ({ ...state, isEnabled: false }));
      return;
    }
    
    // Build AI settings with proper API keys from aiProviders
    const aiSettings: AISettings = {
      ...settings.ai,
      provider: provider as AISettings['provider'],
      selectedModel: grammarCheckModel,  // Use grammar check specific model
      // Set API keys from aiProviders array (primary source)
      openaiApiKey: settings.aiProviders?.find(p => p.id === 'openai')?.apiKey || settings.ai?.openaiApiKey || '',
      anthropicApiKey: settings.aiProviders?.find(p => p.id === 'anthropic')?.apiKey || settings.ai?.anthropicApiKey || '',
      googleApiKey: settings.aiProviders?.find(p => p.id === 'google')?.apiKey || settings.ai?.googleApiKey || '',
      upstageApiKey: settings.aiProviders?.find(p => p.id === 'upstage')?.apiKey || settings.ai?.upstageApiKey || '',
      openrouterApiKey: settings.aiProviders?.find(p => p.id === 'openrouter')?.apiKey || settings.ai?.openrouterApiKey || '',
      cerebrasApiKey: settings.aiProviders?.find(p => p.id === 'cerebras')?.apiKey || settings.ai?.cerebrasApiKey || '',
      groqApiKey: settings.aiProviders?.find(p => p.id === 'groq')?.apiKey || settings.ai?.groqApiKey || '',
      contextWindow: settings.ai?.contextWindow || 8000,
      temperature: settings.ai?.temperature || 0.3,
      maxTokens: settings.ai?.maxTokens || 2048,
      showTokenWarning: settings.ai?.showTokenWarning ?? true,
      tokenWarningThreshold: settings.ai?.tokenWarningThreshold || 80
    };

    // Only initialize if we have valid settings and the AI is globally enabled
    if (aiSettings.provider && aiSettings.selectedModel && hasValidConfig) {
      try {
        // Use V2 service if feature flag is enabled
        if (useNewAIService) {
          aiService = getAIAnalysisServiceV2(aiSettings);
        } else {
          aiService = new AIAnalysisService(aiSettings);
        }

        update(state => ({ ...state, isEnabled: true }));
      } catch (error) {
        console.error('[aiSuggestions] Failed to initialize AI service:', error);
        aiService = null;
        update(state => ({ ...state, isEnabled: false }));
      }
    } else {
      aiService = null;
      update(state => ({ ...state, isEnabled: false }));
    }
  });

  return {
    subscribe,
    
    /**
     * Get the AI analysis service instance for cancellation
     */
    getAIAnalysisService(): AIAnalysisService | null {
      return aiService;
    },
    
    /**
     * Analyze a grammar error with AI
     */
    async analyzeError(grammarError: GrammarError, contextText: string): Promise<AIComment | null> {
      const subscriptionService = SubscriptionService.getInstance();
      
      // Check if user can perform AI analysis
      const canPerform = await subscriptionService.canPerformAction('aiAnalysis');
      if (!canPerform) {
        openUpgradePrompt('feature_locked', 'AI 분석은 프리미엄 기능입니다');
        return null;
      }
      
      if (!aiService) {
        return null;
      }

      // Check if already analyzing this error
      const state = get({ subscribe });
      if (state.analyzingErrors.has(grammarError.id)) {
        return null;
      }

      // Check if we already have a comment for this error
      if (state.comments.has(grammarError.id)) {
        return state.comments.get(grammarError.id) || null;
      }

      update(s => ({
        ...s,
        analyzingErrors: new Set([...s.analyzingErrors, grammarError.id]),
        lastError: null
      }));

      try {
        const comment = await aiService.analyzeGrammarError(grammarError, contextText, false);
        
        update(s => {
          const newAnalyzing = new Set(s.analyzingErrors);
          newAnalyzing.delete(grammarError.id);
          const newComments = new Map(s.comments);
          newComments.set(grammarError.id, comment);
          
          return {
            ...s,
            comments: newComments,
            analyzingErrors: newAnalyzing
          };
        });
        
        // Record successful AI analysis usage
        await subscriptionService.recordUsage('aiAnalysis');

        return comment;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'AI analysis failed';
        
        update(s => {
          const newAnalyzing = new Set(s.analyzingErrors);
          newAnalyzing.delete(grammarError.id);
          
          return {
            ...s,
            analyzingErrors: newAnalyzing,
            lastError: errorMessage
          };
        });
        
        return null;
      }
    },

    /**
     * Analyze multiple errors in batch (optimized to use single AI call)
     */
    async analyzeErrors(errors: GrammarError[], contextText: string): Promise<GrammarError[] | void> {
      // If no errors provided but we have text, do AI-only analysis
      if ((!errors || errors.length === 0) && contextText) {
        return await this.analyzeTextDirectly(contextText);
      }

      const subscriptionService = SubscriptionService.getInstance();

      // Check if user can perform AI analysis
      const canPerform = await subscriptionService.canPerformAction('aiAnalysis');
      if (!canPerform) {
        openUpgradePrompt('feature_locked', 'AI 분석은 프리미엄 기능입니다');
        return;
      }

      if (!aiService) {
        return;
      }

      // Filter out errors already being analyzed or already have comments
      const state = get({ subscribe });
      const errorsToAnalyze = errors.filter(
        error => !state.analyzingErrors.has(error.id) && !state.comments.has(error.id)
      );

      if (errorsToAnalyze.length === 0) {
        return;
      }

      // Mark all errors as being analyzed
      update(s => ({
        ...s,
        analyzingErrors: new Set([...s.analyzingErrors, ...errorsToAnalyze.map(e => e.id)]),
        lastError: null
      }));

      try {
        // Convert GrammarErrors to AIAnalysisRequest format with progress callback
        const request = {
          contextText,
          corrections: errorsToAnalyze.map(error => ({
            original: error.original || error.originalText || '',
            corrected: error.suggestions || (error.corrections?.map(c => c.text) || []),
            help: error.message || error.help || ''
          })),
          userEditedStates: {},
          // Progress callback for incremental UI updates during batch processing
          onProgress: (partialResults: any[], progress: any) => {
            // Show toast notification for batch progress
            if (progress.batchIndex < progress.totalBatches) {
              // Intermediate batches
              toast.info(
                `AI 분석 중... (${progress.current}/${progress.total} 완료)`,
                2000
              );
            } else {
              // Last batch
              toast.success(
                `AI 분석 완료 (${progress.total}개)`,
                3000
              );
            }

            // Update store with partial results as each batch completes
            update(s => {
              const newComments = new Map(s.comments);
              const newAnalyzing = new Set(s.analyzingErrors);

              // Process all accumulated results
              partialResults.forEach((result, index) => {
                if (index < errorsToAnalyze.length) {
                  const error = errorsToAnalyze[index];
                  const comment = {
                    id: `ai-comment-${error.id}`,
                    text: result.reasoning || result.selectedValue || '',
                    confidence: result.isExceptionProcessed ? 0.5 : 0.9,
                    type: 'suggestion' as const,
                    selectedValue: result.selectedValue,
                    // If keepOriginal is true, AI disagrees with the correction
                    agreesWithCorrection: !result.keepOriginal,
                    suggestsKeepOriginal: result.keepOriginal || false,
                    timestamp: Date.now()
                  };

                  newComments.set(error.id, comment);
                  // Remove analyzing state immediately for completed items
                  // This allows UI to display results incrementally
                  newAnalyzing.delete(error.id);
                }
              });

              return {
                ...s,
                comments: newComments,
                analyzingErrors: newAnalyzing
              };
            });
          }
        };

        // Single AI call for all errors (batch processing with progress updates)
        const results = await aiService.analyzeCorrections(request, false);

        // Final update to ensure all analyzing states are cleared
        update(s => {
          const newComments = new Map(s.comments);
          const newAnalyzing = new Set(s.analyzingErrors);

          results.forEach((result) => {
            // Use correctionIndex to map to the correct error
            const correctionIndex = result.correctionIndex;
            if (correctionIndex >= 0 && correctionIndex < errorsToAnalyze.length) {
              const error = errorsToAnalyze[correctionIndex];
              const comment = {
                id: `ai-comment-${error.id}`,
                text: result.reasoning || result.selectedValue || '',
                confidence: result.isExceptionProcessed ? 0.5 : 0.9,
                type: 'suggestion' as const,
                selectedValue: result.selectedValue,
                // If keepOriginal is true, AI disagrees with the correction
                agreesWithCorrection: !result.keepOriginal,
                suggestsKeepOriginal: result.keepOriginal || false,
                timestamp: Date.now()
              };

              newComments.set(error.id, comment);
              newAnalyzing.delete(error.id);
            }
          });

          return {
            ...s,
            comments: newComments,
            analyzingErrors: newAnalyzing
          };
        });

        // Record usage ONCE for the entire batch
        await subscriptionService.recordUsage('aiAnalysis');

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Batch AI analysis failed';

        update(s => {
          const newAnalyzing = new Set(s.analyzingErrors);
          errorsToAnalyze.forEach(e => newAnalyzing.delete(e.id));

          return {
            ...s,
            analyzingErrors: newAnalyzing,
            lastError: errorMessage
          };
        });
      }
    },

    /**
     * Analyze text directly with AI (without existing grammar check results)
     */
    async analyzeTextDirectly(text: string): Promise<GrammarError[]> {
      const subscriptionService = SubscriptionService.getInstance();
      
      // Check rate limit first (for server-side limits)
      const { usageTracker } = await import('$lib/services/UsageTrackingService');
      const rateLimitCheck = await usageTracker.checkRateLimit('ai');
      
      // Debug logging
      
      if (!rateLimitCheck.allowed) {
        // Check if it's a daily limit or per-minute limit
        if (rateLimitCheck.daily && rateLimitCheck.daily.remaining === 0) {
          openUpgradePrompt('limit_reached', 'AI 분석 일일 한도 도달');
        } else {
          // Per-minute limit - pass the actual rate limit data
          // Check for minute field first (correct), then hourly (incorrect but exists)
          const minuteLimit = rateLimitCheck.minute || (rateLimitCheck as any).perMinute;
          const limit = minuteLimit?.limit || rateLimitCheck.hourly?.limit || 5;
          const used = minuteLimit?.used || rateLimitCheck.hourly?.used || 5;
          
          const details = {
            type: 'rate',
            used: used,
            limit: limit,
            resetIn: 60 // Default to 60 seconds
          };
          openUpgradePrompt('limit_reached', JSON.stringify(details));
        }
        return [];
      }
      
      // Check if user can perform AI analysis (local subscription check)
      const canPerform = await subscriptionService.canPerformAction('aiAnalysis');
      if (!canPerform) {
        const remaining = subscriptionService.getRemainingUses('aiAnalysis');
        if (remaining === 0) {
          openUpgradePrompt('limit_reached', 'AI 분석 일일 한도 도달');
        } else {
          openUpgradePrompt('feature_locked', 'AI 분석은 프리미엄 기능입니다');
        }
        return [];
      }
      
      // Note: Text length check is already done in AIModelSelector before calling this
      // We skip it here to avoid duplicate prompts
      
      if (!aiService) {
        console.error('[aiSuggestionsStore] AI service not initialized');
        return [];
      }
      
      update(s => ({
        ...s,
        lastError: null
      }));

      try {
        const { errors, comments } = await aiService.analyzeText(text, false);
        
        // Store the AI comments
        update(s => {
          const newComments = new Map(s.comments);
          comments.forEach((comment, errorId) => {
            newComments.set(errorId, comment);
          });
          
          return {
            ...s,
            comments: newComments
          };
        });

        // Also update the error store with AI-found errors
        const { grammarActions } = await import('./grammar');
        
        // Use grammarActions.setErrors which has built-in merge logic
        // Pass false for mergeWithAI since these ARE the AI errors
        // If there are existing grammar errors, they should be preserved
        grammarActions.setErrors(errors, true);
        
        // Record successful AI analysis usage (both local and server)
        await subscriptionService.recordUsage('aiAnalysis');
        
        // Also track to server for rate limiting
        await usageTracker.trackAIUsage({
          model: aiService.getCurrentModel() || 'unknown',
          provider: aiService.getCurrentProvider() || 'openrouter',
          tokens: text.length, // Approximate
          cost: 0, // OpenRouter free tier
          responseTime: 0,
          success: true
        });

        return errors;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'AI analysis failed';
        
        // Check if it's a rate limit error
        if (error instanceof Error && (error.message.includes('429') || error.message.includes('Rate limit'))) {
          // Check if it's a daily limit or per-minute limit
          if (error.message.includes('분당') || error.message.includes('minute')) {
            openUpgradePrompt('limit_reached', '잠시 후 다시 시도해주세요 (분당 한도 초과)');
          } else {
            openUpgradePrompt('limit_reached', 'AI 분석 일일 한도 도달');
          }
        }
        
        update(s => ({
          ...s,
          lastError: errorMessage
        }));
        
        console.error('AI text analysis failed:', error);
        return [];
      }
    },

    /**
     * Get AI comment for an error
     */
    getComment(errorId: string): AIComment | undefined {
      const state = get({ subscribe });
      return state.comments.get(errorId);
    },

    /**
     * Check if an error is being analyzed
     */
    isAnalyzing(errorId: string): boolean {
      const state = get({ subscribe });
      return state.analyzingErrors.has(errorId);
    },

    /**
     * Clear all AI comments
     */
    clearComments(): void {
      update(state => ({
        ...state,
        comments: new Map(),
        analyzingErrors: new Set()
      }));
    },

    /**
     * Remove comment for specific error
     */
    removeComment(errorId: string): void {
      update(state => {
        const newComments = new Map(state.comments);
        newComments.delete(errorId);
        return { ...state, comments: newComments };
      });
    }
  };
}

export const aiSuggestionsStore = createAISuggestionsStore();

// Derived store for checking if any analysis is in progress
export const isAnalyzingAny = derived(
  aiSuggestionsStore,
  $store => $store.analyzingErrors.size > 0
);

// Derived store for total number of AI comments
export const totalAIComments = derived(
  aiSuggestionsStore,
  $store => $store.comments.size
);